type BookOfferPageProps = {
  onLogin: () => void;
};

const STRIPE_PAYMENT_LINK = "https://buy.stripe.com/14AcN54Pl15028X0ba7wA00";

const included = [
  "Paperback copy of Calm But Capable",
  "UK postage and packaging",
  "Free Calm But Capable workshop access",
  "Optional 15–20-minute Founder Guidance Call, valued at £50",
];

const readerSignals = [
  "You know what you want to change, but still struggle to begin",
  "You feel left behind when other people seem to move forward more easily",
  "You have tried before, lost momentum and begun to doubt yourself",
  "You feel capable, yet stress, fear or mental clutter keeps getting in the way",
  "You are tired of advice that tells you to push harder without understanding the problem",
  "You want practical tools and a version of success that genuinely belongs to you",
];

const bookBenefits = [
  ["Why capable people can still feel stuck", "Understand why knowing what to do is not always enough—and why difficulty beginning does not mean you are lazy, weak or incapable."],
  ["What has shaped the way you see yourself", "Recognise how repeated messages, experiences, expectations and labels can quietly become limits you never consciously chose."],
  ["How to find the barrier that actually matters", "Look beneath procrastination, lost confidence and apparent lack of motivation to identify what is really preventing progress."],
  ["How to define success for yourself", "Stop measuring your life against somebody else’s version of success and decide what becoming your own “Best Me” genuinely means."],
  ["Practical tools you can use in real life", "Build a personal toolkit using reflection, memory techniques, visualisation, routines and ways to create calm when your thoughts feel crowded."],
  ["How to take a step you can actually sustain", "Turn insight into one realistic action—small enough to begin, meaningful enough to matter and capable of building evidence that change is possible."],
];

const insideBook = [
  "Why capable people can still feel stuck.",
  "How experiences, expectations and labels can shape what you believe is possible.",
  "How to identify what is genuinely preventing you from moving forward.",
  "How to define success and your own “Best Me” for yourself.",
  "Practical tools to build your toolkit for success.",
  "How to choose one clear, achievable step and begin building momentum.",
  "How to put everything together so you can experience success in whatever you choose to work towards.",
];

export default function BookOfferPage({ onLogin }: BookOfferPageProps) {
  return (
    <main className="public-home book-page">
      <header className="public-header">
        <a className="public-brand" href="/" aria-label="Calm But Capable home">
          <img src="/logo-white.png" alt="" className="public-logo" />
          <span>Calm But Capable</span>
        </a>
        <div className="organisation-header-actions">
          <a className="organisation-back-link" href="/">Home</a>
          <button className="public-login" type="button" onClick={onLogin}>Login</button>
        </div>
      </header>

      <section className="book-hero">
        <div className="book-hero-copy">
          <p className="public-kicker">For capable people who feel stuck</p>
          <h1>There may be nothing wrong with you.</h1>
          <p className="book-lead">
            Perhaps you have watched other people move forward while you seem to
            remain in the same place. You have made plans, promised yourself that
            this time will be different and wondered why something that appears
            easy for others can feel so difficult for you.
          </p>

          <div className="book-hero-origin">
            <p>
              I was sitting alone in front of my computer at a low point in my
              life. I started asking questions—of myself and of the world.
            </p>
            <p>
              Over the following twelve months, I looked at what had made me
              successful, what I had learned through failure and what decades of
              working with mentors had taught me. I began writing a piece of
              software that would allow other people to experience the success
              they were looking for. The result was Calm But Capable.
            </p>
            <p>
              This book shares the lessons I have learned through decades of
              working with mentors and experiencing both success and failure.
            </p>
          </div>

          <p className="book-hero-promise book-hero-promise--central">
            This book will take you on that journey so you can experience the
            success you desire, yet may have failed to experience.
          </p>

          <div className="book-price-row" aria-label="Book offer price">
            <span className="book-price">£5.99</span>
            <span className="book-price-note">including UK delivery</span>
          </div>

          <div className="book-founder-bonus">
            <p className="book-founder-bonus__label">Included free with your book</p>
            <p className="book-founder-bonus__title">A private 15–20-minute Zoom guidance call with Nicholas Castle</p>
            <p className="book-founder-bonus__copy">
              Bring one issue you want to move forward with. Together, we will
              explore what may be getting in your way and identify one clear,
              achievable next step.
            </p>
            <p className="book-founder-bonus__value">Founder call valued at £50</p>
          </div>

          <a className="book-button" href={STRIPE_PAYMENT_LINK}>
            Get your copy for £5.99 <span aria-hidden="true">→</span>
          </a>
          <p className="book-secure-note">Secure checkout provided by Stripe. UK delivery only. The call is optional, limited and subject to availability.</p>
        </div>

        <div className="book-hero-aside">
          <img
            className="book-mockup"
            src="/calm-but-capable-book-mockup.webp"
            alt="Calm But Capable paperback by Nicholas Castle"
          />
          <blockquote className="book-testimonial">
            <p>“I couldn’t put it down. It was so easy to read—I wanted to know what was next.”</p>
            <footer>— Sylwia, early reader</footer>
          </blockquote>
          <aside className="book-offer-card" aria-label="What the offer includes">
            <p className="public-card-label">Introductory founder offer</p>
            <p className="book-value">Over £60 of value</p>
            <ul>
              {included.map((item) => <li key={item}>{item}</li>)}
            </ul>
            <p className="book-availability">Founder calls are optional, limited and subject to availability.</p>
          </aside>
        </div>
      </section>

      <section className="book-section book-inside-summary">
        <div className="book-section-heading">
          <p className="public-kicker">Inside Calm But Capable</p>
          <h2>This is what you will discover.</h2>
        </div>
        <ul className="book-inside-list">
          {insideBook.map((item, index) => (
            <li key={item} className={index === insideBook.length - 1 ? "book-inside-list__outcome" : ""}>
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section className="book-recognition">
        <div className="book-section-heading">
          <p className="public-kicker">Does this sound familiar?</p>
          <h2>This book may be for you if…</h2>
        </div>
        <div className="book-reader-grid">
          {readerSignals.map((signal) => <div key={signal}>{signal}</div>)}
        </div>
        <div className="book-emotional-bridge">
          <p>
            Repeated false starts can do more than delay progress. They can make
            you doubt yourself. Each unfinished plan can begin to feel like
            evidence that you are the problem—even when the approach, the timing
            or the support was wrong for you.
          </p>
          <p>
            This book offers a different starting point: understanding before
            judgement. Your past may have shaped your path, but it does not have
            to define the limit of who you can become.
          </p>
        </div>
      </section>

      <section className="book-section book-introduction">
        <div>
          <p className="public-kicker">This is not another motivational book</p>
          <h2>Before you push harder, understand what you are pushing against.</h2>
        </div>
        <div>
          <p>
            Most advice begins with action: be more disciplined, think positively,
            set bigger goals or work harder. But advice cannot help when it is
            aimed at the wrong problem.
          </p>
          <p>
            This book begins with understanding. It helps you examine the box
            built around you by experiences, expectations and labels, decide
            which parts no longer belong, and remove one brick at a time. You are
            not asked to become somebody new. You are helped to recognise and use
            more of the capability already within you.
          </p>
        </div>
      </section>

      <section className="book-section book-benefits">
        <div className="book-section-heading">
          <p className="public-kicker">What is inside the book</p>
          <h2>Answers, tools and a way to move forward.</h2>
          <p>This is what the book will help you understand and begin applying to your own life.</p>
        </div>
        <div className="book-benefit-grid">
          {bookBenefits.map(([title, description], index) => (
            <article key={title}>
              <span>0{index + 1}</span>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="book-section book-path">
        <div className="book-section-heading">
          <p className="public-kicker">What happens next</p>
          <h2>A book, a workshop and a conversation</h2>
          <p>Each part helps you move from understanding towards one useful next step.</p>
        </div>
        <ol className="book-step-grid">
          <li><span>1</span><h3>Read in your own time</h3><p>Use the book as a practical tool rather than something to rush through.</p></li>
          <li><span>2</span><h3>Access the free workshop</h3><p>Explore one clear step and begin applying the approach for yourself.</p></li>
          <li><span>3</span><h3>Choose whether to talk</h3><p>Use the optional founder call to explore one issue and identify an achievable next step.</p></li>
        </ol>
      </section>

      <section className="book-section book-call">
        <div>
          <p className="public-kicker">The difference it can make</p>
          <h2>Finish with greater clarity—not another list of things you “should” be doing.</h2>
        </div>
        <div>
          <p>
            By the end of the book, you should be better able to separate your
            capability from the difficulties you have experienced, recognise a
            barrier that has been holding you back and decide what progress means
            for you.
          </p>
          <p>
            Most importantly, you will have a practical way to choose one clear
            next step and begin building evidence that you can move forward.
          </p>
        </div>
      </section>

      <section className="book-section book-call">
        <div>
          <p className="public-kicker">Focused guidance</p>
          <h2>One issue. One useful insight. One clear next step.</h2>
        </div>
        <div>
          <p>
            The optional Founder Guidance Call is a focused 15–20-minute
            conversation with Nicholas Castle. It is designed to help you
            consider what may be getting in your way and identify a realistic
            next step—not to sell you something under pressure.
          </p>
          <p>The call is valued at £50 and is offered subject to availability.</p>
        </div>
      </section>

      <section className="book-section book-founder">
        <div className="organisation-founder-mark" aria-hidden="true">NC</div>
        <div>
          <p className="public-kicker">Written by Nicholas Castle</p>
          <h2>Experience of people—not assumptions about them.</h2>
          <p>
            Nicholas Castle is the founder of Calm But Capable. His experience
            as an investigator, teacher and mentor taught him to look beyond
            what appears obvious. His own recovery from complex PTSD reinforced
            a belief that has shaped this book: people are often more capable
            than they appear—and more capable than they realise.
          </p>
        </div>
      </section>

      <section className="book-section book-reassurance">
        <div className="book-section-heading">
          <p className="public-kicker">Before you order</p>
          <h2>A clear, straightforward offer.</h2>
        </div>
        <div className="book-faq-grid">
          <article><h3>Is the guidance call compulsory?</h3><p>No. The call is entirely optional. You can simply read the book and use the workshop.</p></article>
          <article><h3>Will the call become a sales pitch?</h3><p>No. Its purpose is to explore one issue and help you identify one realistic next step.</p></article>
          <article><h3>Is this therapy or medical treatment?</h3><p>No. Calm But Capable is a personal-development approach and does not replace professional medical or therapeutic support.</p></article>
          <article><h3>What does £5.99 include?</h3><p>The paperback, UK delivery, free workshop access and the optional founder call. There is no additional postage charge.</p></article>
        </div>
      </section>

      <section className="book-final-cta">
        <p className="public-kicker">Your next step can be a small one</p>
        <h2>You have not failed. You may simply need a different way forward.</h2>
        <p>Begin with the book: paperback, UK delivery, workshop access and an optional founder call—all for £5.99. The first release is limited to 10 copies.</p>
        <a className="book-button book-button--light" href={STRIPE_PAYMENT_LINK}>Get your copy for £5.99</a>
      </section>

      <footer className="organisation-footer">
        <a className="public-brand" href="/"><img src="/logo-white.png" alt="" className="public-logo" /><span>Calm But Capable</span></a>
        <p>Understanding before action. Capability before limitation.</p>
      </footer>
    </main>
  );
}
